import {
  DOSYA_HAT_ADI,
  FILO_YERI_ADI,
  KALEM_HATTI,
  beyanOnDoldurmaRn,
  kimlikGecerliMi,
  pkgNo,
  sureklilikSonucMetni,
  type AdaySonucu,
  type CkRolKaynagi,
  type FiloYeri,
  type KokenDurumu,
  type MontajBagGovdesi,
  type MontajBagTuru,
  type MontajBagYaniti,
  type MontajBagi,
  type MontajKaydiYaniti,
  type MontajRol,
  type MontajSureklilikYaniti,
  type SureklilikAdayi,
  type SureklilikHucresi,
  type SureklilikIsareti,
  type SureklilikKalemi,
  type SureklilikSatiri,
  type SureklilikSonucu,
} from "@oasis/domain";
import { api } from "@/lib/api";
import { bicimDili, cevir } from "@/lib/dil";
import type { Ton } from "@/lib/mrb-toplu";

/**
 * D-068 (üçüncü teslim): riserın Assembly sayfasındaki "Continuity" kutusunun ve "Small parts, side by side" kutusunun saf
 * mantığı (`MontajSureklilik.tsx` yalnız çizer). Yol kurucular, sonuç ve beyan çiplerinin metin ve tonları, hücre satırları, işaret
 * cümleleri, aday satırları, beyan penceresinin ön-doldurması ve beyan gövdesi burada; JSX yok.
 *
 * Karşılaştırma sunucuda HESAPLANIR (`GET /api/montaj/:id/sureklilik`), saklanmaz; ekran yeniden hesaplamaz, yalnız gösterir. Aday
 * yalnız GÖSTERİLİR: beyan penceresinde verici riser girişini en çok önceden doldurur, kişi "Confirm"e basmadan hiçbir şey yazılmaz.
 * Bağ yalnız `mrb` yetkili kişinin beyanıyla kurulur (sunucu denetler; düğmeler `Buton islem="mrb"`). Ekran metinleri İngilizce ve
 * `cevir` içindedir; hat adları (Main Tube, Choke, Kill, Booster, Hydraulic) ve FM-0012'nin basılı parça adları çevrilmez.
 */

/* ------------------------------------------------------------ tipler ------------------------------------------------------------ */

/** Okuma yanıtının bir satırı: karşılaştırma + adaylar + köken uyumu. */
export type SureklilikSatirYaniti = MontajSureklilikYaniti["satirlar"][number];
export interface EtiketGorunumu {
  metin: string;
  ton: Ton;
}

/* ------------------------------------------------------------ uçlar ------------------------------------------------------------ */

export const SUREKLILIK_UCU = {
  /** GET: kutunun verisi (`MontajSureklilikYaniti`) */
  oku: (montajId: string) => `/montaj/${montajId}/sureklilik`,
  /** POST `MontajBagGovdesi` (`MontajBagYaniti`; yetki `mrb`) */
  bag: (birimId: string) => `/montaj/birim/${birimId}/bag`,
  /** POST `{ sn }` (`MontajBagYaniti`; yetki `mrb`) */
  bagKaldir: (birimId: string) => `/montaj/birim/${birimId}/bag/kaldir`,
} as const;

/** Beyanı yazar. Sunucunun ret cümlesi (İngilizce) hata mesajı olarak, çevrilmiş gelir. */
export const montajBagYaz = (birimId: string, govde: MontajBagGovdesi) => api.post<MontajBagYaniti>(SUREKLILIK_UCU.bag(birimId), govde);
/** Beyanı geri alır; `sn` bayat sayfa korumasıdır (ekranda görülen takılan kimlik). */
export const montajBagKaldir = (birimId: string, sn: string | null) => api.post<MontajBagYaniti>(SUREKLILIK_UCU.bagKaldir(birimId), { sn });

/**
 * Kutunun yeniden okunması gereken an: kaydın hâli, girdisi ya da girdi belgesi değişti (yeni okuma). Sayfa kaydı tazeleyince
 * kutu bu değer değiştiyse kendi verisini yeniden okur.
 */
export const sureklilikSurumu = (k: Pick<MontajKaydiYaniti, "hal" | "kapali" | "girdi" | "girdiBelgesi">): string =>
  [k.hal, k.kapali ? "1" : "0", k.girdi?.ts ?? "", k.girdiBelgesi?.dokumanId ?? ""].join("|");

/**
 * Continuity kutusu çizilir mi (plan D §5.1, §9.2): yalnız tamamlanmış kayıtta, yani girdisi okunmuş rapor kaydında (`okundu`) ve
 * imzalı çıktısıyla kapanmış formda (`form_kapali`). Girdi beklenirken, girdisiz tamamlanmış kayıtta (karşılaştırılacak birim
 * yok), form açıkken ve imzalı çıktısı beklenirken çizilmez.
 */
export const sureklilikKutusuCizilir = (hal: MontajKaydiYaniti["hal"]): boolean => hal === "okundu" || hal === "form_kapali";

/** İşlemin düştüğü hata cümlesi (sunucunun cümlesi çevrilmiş gelir); mesajsız hatada genel cümle. */
export const islemHatasi = (e: unknown): string => (e instanceof Error && e.message ? e.message : cevir("Operation failed"));

/**
 * Beyan işleminin akışı (beyan, kaldırma, yeniden beyan): işlem yürür; başarıda yanıtın uyarıları, düşerse hata cümlesi döner.
 * Ardından HER durumda verilen tazeleyiciler sırayla çağrılır: önce kutunun kendi verisi, sonra sayfanın kaydı. Beyan kaydın
 * "Remove" düğmesini de belirler (beyanı olan kayıt kaldırılmaz; `kaldirilabilir` / `kaldirmaEngeli`, plan D §4.7): sayfa
 * tazelenmezse düğme eski hâlinde kalır. İşlem düşse de tazelenir (bayat sayfa ya da kaldırılmış beyan ekranda görünsün).
 * Tazeleyicinin hatası yutulur: kutu kendi yükleme hatasını gösterir, sayfa bir sonraki tazelemede düzelir.
 */
export async function beyanIslemi(
  is: () => Promise<MontajBagYaniti>,
  tazeleyiciler: readonly ((() => Promise<unknown> | void) | undefined)[],
): Promise<{ uyarilar: string[]; hata: string | null }> {
  let sonuc: { uyarilar: string[]; hata: string | null };
  try {
    sonuc = { uyarilar: (await is()).uyarilar, hata: null };
  } catch (e) {
    sonuc = { uyarilar: [], hata: islemHatasi(e) };
  }
  for (const t of tazeleyiciler) {
    try {
      await t?.();
    } catch {
      // yutulur (yukarıdaki gerekçe)
    }
  }
  return sonuc;
}

/* ------------------------------------------------------------ biçim ------------------------------------------------------------ */

/**
 * ISO gün ("2026-05-04") ya da zaman damgası → ekrandaki gün ("04/05/2026"). Yalnız gün taşıyan değer yerel takvimde kurulur:
 * saat dilimi günü bir öncekine kaydırmasın. Boş değer "—"; okunamayan değer olduğu gibi.
 */
export function gunMetni(v: string | null | undefined): string {
  if (!v) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString(bicimDili());
}

/** Kalemin hat adı (çevrilmez): Main Tube · Choke · Kill · Booster · Hydraulic. */
export const kalemHatAdi = (kalem: SureklilikKalemi): string => DOSYA_HAT_ADI[KALEM_HATTI[kalem]];

/**
 * Tablonun "Item" hücresi. C ve K satırında rol formda yazmıyor ve hatlar yazılış sırasıyla yerleştiyse (`rolKaynagi = "sira"`)
 * ad "Choke" / "Kill" değil, formdaki sırasıdır: C satırı 1., K satırı 2.
 */
export function kalemAdi(s: { kalem: SureklilikKalemi; rolKaynagi: CkRolKaynagi | null }): string {
  if (s.rolKaynagi === "sira" && s.kalem === "C") return cevir("C&K line, 1st on the form");
  if (s.rolKaynagi === "sira" && s.kalem === "K") return cevir("C&K line, 2nd on the form");
  return kalemHatAdi(s.kalem);
}

/* ------------------------------------------------------------ sonuç ------------------------------------------------------------ */

export const SONUC_TONU: Readonly<Record<SureklilikSonucu, Ton>> = {
  ayni: "ok",
  yazim_farki: "info",
  rol_farki: "warn",
  farkli: "bad",
  belirsiz: "warn",
  kaynak_yok: "neutral",
};

/**
 * Sonuç çipi. Metin alan paketindedir (`sureklilikSonucMetni`): `rol_farki` tek hatta ise "same line, now in the other slot", iki
 * hat karşılıklı yer değiştirdiyse "same line, Choke and Kill swapped".
 */
export const sonucCipi = (s: Pick<SureklilikSatiri, "sonuc" | "rolFarki">): EtiketGorunumu => ({ metin: cevir(sureklilikSonucMetni(s)), ton: SONUC_TONU[s.sonuc] });

/** Sonucun dayandığı kaynak (gri satır); `kaynak_yok`'ta yok. */
export function dayanakMetni(d: SureklilikSatiri["dayanak"]): string | null {
  if (d === "sokum") return cevir("compared with the Disassembly record");
  if (d === "oem") return cevir("compared with the OEM record");
  return null;
}

/** Sayaç (kutunun sağ üstü): sonucu `farkli`, `rol_farki` ya da `belirsiz` olan kalem ve beyanlı kalem sayısı. */
export function sayacMetni(satirlar: readonly Pick<SureklilikSatiri, "sonuc" | "bag">[]): string {
  const farkli = satirlar.filter((s) => s.sonuc === "farkli" || s.sonuc === "rol_farki" || s.sonuc === "belirsiz").length;
  return cevir("different: {0} of 5 · declared: {1}", farkli, satirlar.filter((s) => s.bag).length);
}

/** Sonuç hücresindeki işaret cümlesi (her biri ayrı satır). `deger` cümledeki değerdir; `rol_yazilmamis`'te yuvanın kaynağıdır. */
export function isaretMetni(i: { kod: SureklilikIsareti; deger?: string }): string {
  const d = i.deger ?? "";
  switch (i.kod) {
    case "kendi_degerine_yakin":
      return cevir("one character away from this riser's own value {0}: check the form", d);
    case "rol_yazilmamis":
      return i.deger === "diger_hat" ? cevir("role not written on the form: placed as the counterpart of the other line") : cevir("role not written on the form: placed by the Disassembly record");
    case "rol_sirayla":
      return cevir("role not written on the form: lines are shown in the order written");
    case "rol_cift":
      return cevir("both lines carry the same role letter on the form");
    case "sokum_farkli":
      return cevir("same as the OEM record; the Disassembly record reads {0}", d);
    case "oem_farkli":
      return cevir("same as removed; differs from the OEM record {0}", d);
    case "cikan_is_emri":
      return cevir("written as a work order number at disassembly: {0}. It is not counted as an identity.", d);
    case "cikan_is_emri_ayni_sayi":
      return cevir("the number after WO is the same as the fitted serial number");
    case "cikan_eski_yok":
      return cevir("no old identity on the Disassembly record, only the new ID {0}", d);
    case "mt_weldment":
      return cevir("the MAIN BARREL cell carries the MT weldment SN");
    case "mt_alt_parca":
      return cevir("the MAIN BARREL cell carries the MT box, pipe or pin identities");
    case "baslik_farkli":
      return cevir("the Assembly Serial No. box reads {0}", d);
    case "okuma_belirsiz":
      return cevir("uncertain reading on the form");
    case "kimlik_okunamadi":
      return cevir("no serial number could be read from: {0}", d);
    case "mrb_diger":
      return cevir("appears in this riser's MRB as another value: {0}", d);
    case "sokuldu":
      return cevir("removed after assembly");
    case "ayni_sn_iki_birim":
      return cevir("the two C&K lines carry the same serial number");
    case "fazla_birim":
      return cevir("the assembly record lists {0} more units on this row; the first two are compared", d);
    case "sayfa_eksik":
      return cevir("page {0} of the report was not scanned", d);
  }
}

/* ------------------------------------------------------------ hücreler ------------------------------------------------------------ */

/**
 * Tablonun OEM, çıkan ya da takılan hücresi. `ana` eş aralıklı yazılır (temiz kimlik; kimlik okunamadıysa gri yazım); `ana` yoksa
 * gri `bos` metni. `cipler` ana satırın yanında, `alt` altında gri satırlardır.
 */
export interface HucreGorunumu {
  ana: string | null;
  /** ana satır kimlik değil, yazımdır (gri) */
  yazim: boolean;
  bos: string;
  cipler: EtiketGorunumu[];
  alt: string[];
}

const isaretVar = (s: Pick<SureklilikSatiri, "isaretler">, kod: SureklilikIsareti): boolean => s.isaretler.some((i) => i.kod === kod);
const ekler = (h: SureklilikHucresi, tur: SureklilikHucresi["ek"][number]["tur"]): string[] => h.ek.filter((e) => e.tur === tur).map((e) => e.deger);
const yazildigiGibi = (h: SureklilikHucresi): string[] => (h.yazim?.trim() && h.yazim.trim() !== h.sn ? [cevir("as written: {0}", h.yazim.trim())] : []);

function oemHucresi(h: SureklilikHucresi | null): HucreGorunumu {
  const bos: HucreGorunumu = { ana: null, yazim: false, bos: "—", cipler: [], alt: [] };
  if (!h) return { ...bos, bos: cevir("no MRB content") };
  // Choke / Kill MRB'de atanmamış ve hücre hattı bulamadıysa: iki hat birlikte ("2A … / 2B …").
  const atanmamis = ekler(h, "oem_atanmamis");
  if (!h.sn && atanmamis.length) return { ...bos, ana: atanmamis.join(" / "), alt: [cevir("Choke / Kill not assigned in the MRB")] };
  return {
    ...bos,
    ana: h.sn,
    alt: [...ekler(h, "mt_weldment").map((d) => cevir("MT weldment {0}", d)), ...ekler(h, "oem_hat"), ...ekler(h, "oteki_deger")],
  };
}

function cikanHucresi(h: SureklilikHucresi | null, s: Pick<SureklilikSatiri, "isaretler">, sokumDurumu: MontajSureklilikYaniti["kaynaklar"]["sokum"]["durum"]): HucreGorunumu {
  const bos: HucreGorunumu = { ana: null, yazim: false, bos: "—", cipler: [], alt: [] };
  if (!h) return { ...bos, bos: sokumDurumu === "yok" ? cevir("no Disassembly record") : "—" };
  const sonra = [...ekler(h, "yeni_id").map((d) => cevir("new ID {0}", d)), ...ekler(h, "tarih").map((d) => cevir("removed {0}", gunMetni(d)))];
  // Sökümde iş emri numarası yazılmış: kimlik sayılmaz; temiz kimlik yerine yazım ve çip.
  const isEmri = s.isaretler.find((i) => i.kod === "cikan_is_emri");
  if (isEmri) return { ...bos, ana: h.yazim ?? isEmri.deger ?? null, cipler: [{ metin: cevir("work order number, not an identity"), ton: "neutral" }], alt: sonra };
  if (!h.sn && h.yazim?.trim()) return { ...bos, ana: h.yazim.trim(), yazim: true, alt: [...ekler(h, "oteki_deger"), ...sonra] };
  return { ...bos, ana: h.sn, alt: [...(h.sn ? yazildigiGibi(h) : []), ...ekler(h, "oteki_deger"), ...sonra] };
}

function takilanHucresi(h: SureklilikHucresi | null, s: Pick<SureklilikSatiri, "isaretler">): HucreGorunumu {
  const bos: HucreGorunumu = { ana: null, yazim: false, bos: "—", cipler: [], alt: [] };
  const cipler: EtiketGorunumu[] = [
    ...(isaretVar(s, "okuma_belirsiz") ? [{ metin: cevir("uncertain reading"), ton: "warn" as const }] : []),
    ...(isaretVar(s, "sokuldu") ? [{ metin: cevir("removed after assembly"), ton: "neutral" as const }] : []),
    ...(isaretVar(s, "sayfa_eksik") ? [{ metin: cevir("page not scanned"), ton: "warn" as const }] : []),
  ];
  if (!h) return { ...bos, cipler };
  const kutu = ekler(h, "baslik_kutusu").map((d) => cevir("Assembly Serial No. box: {0}", d));
  if (!h.sn) {
    const yazim = h.yazim?.trim();
    return yazim ? { ...bos, ana: yazim, yazim: true, cipler: [{ metin: cevir("no serial number read"), ton: "warn" }, ...cipler], alt: kutu } : { ...bos, cipler, alt: kutu };
  }
  return { ...bos, ana: h.sn, cipler, alt: [...yazildigiGibi(h), ...ekler(h, "oteki_deger"), ...kutu] };
}

/** Satırın bir kolonunun hücresi: "OEM record" · "Removed (disassembly)" · "Fitted (assembly)". */
export function hucreSatirlari(sutun: "oem" | "cikan" | "takilan", s: SureklilikSatiri, kaynaklar: Pick<MontajSureklilikYaniti["kaynaklar"], "sokum">): HucreGorunumu {
  if (sutun === "oem") return oemHucresi(s.oem);
  if (sutun === "cikan") return cikanHucresi(s.cikan, s, kaynaklar.sokum.durum);
  return takilanHucresi(s.takilan, s);
}

/** Kutunun üstündeki üç kaynak çipi; söküm kaydı varsa çip kaydın sayfasına bağlanır (`sokumId`). */
export function kaynakCipleri(k: MontajSureklilikYaniti["kaynaklar"]): { metin: string; sokumId: string | null }[] {
  const oem = k.oem.durum === "var" ? cevir("OEM record: MRB {0}", k.oem.mrbSn ?? "—") : k.oem.durum === "icerik_yok" ? cevir("OEM record: no MRB content") : cevir("OEM record: no MRB record");
  const sokum = k.sokum.durum === "var" ? cevir("Disassembly record: {0}", gunMetni(k.sokum.tarih)) : k.sokum.durum === "satir_yok" ? cevir("Disassembly record: no rows") : cevir("Disassembly record: none");
  const montaj = k.montaj.tarih ? cevir("Assembly: {0}", gunMetni(k.montaj.tarih)) : cevir("Assembly: date not read");
  return [
    { metin: oem, sokumId: null },
    { metin: sokum, sokumId: k.sokum.durum !== "yok" ? k.sokum.sokumId : null },
    { metin: montaj, sokumId: null },
  ];
}

/* ------------------------------------------------------------ adaylar ------------------------------------------------------------ */

/** Adayların tümünün gösterildiği sonuçlar (takılan kimlik dolu); öteki sonuçlarda yalnız başka montaj kaydındaki tam eşleşme (çelişki). */
const ADAY_SONUCLARI: readonly SureklilikSonucu[] = ["farkli", "belirsiz", "kaynak_yok"];

/**
 * Aday bloğu satırın altında çizilir mi (plan D §3.1): takılan kimlik geçerli olmalı; sonuç `farkli`, `belirsiz` ya da `kaynak_yok`
 * ise her zaman ("Not found…" de bir yanıttır); `ayni`, `yazim_farki`, `rol_farki` ise yalnız başka riserın montaj kaydında tam eşleşme
 * varsa (aynı numara iki montaj kaydında: ikisi birden doğru olamaz).
 */
export function adayGosterilirMi(s: Pick<SureklilikSatirYaniti, "sonuc" | "takilan" | "adaylar">): boolean {
  if (!kimlikGecerliMi(s.takilan?.sn)) return false;
  if (ADAY_SONUCLARI.includes(s.sonuc)) return true;
  return s.adaylar.tam.some((a) => a.kaynak === "montaj");
}

/** Aday yeri (FiloYeri) ekran adıyla; hat adları çevrilmez. */
const yerAdi = (yer: FiloYeri | undefined): string => {
  if (!yer) return "";
  if (yer === "C" || yer === "K" || yer === "B" || yer === "H") return FILO_YERI_ADI[yer];
  return cevir(FILO_YERI_ADI[yer]);
};

/** Bir aday satırının görünümü: metin, rozet ("exact"), zaman ya da çelişki cümlesi ve kaydın sayfasına bağlantı. */
export interface AdayGorunumu {
  metin: string;
  rozet: EtiketGorunumu | null;
  /** söküm adayında zaman cümlesi; montaj adayında çelişki cümlesi. `bad` kırmızı yazılır. */
  not: EtiketGorunumu | null;
  baglanti: { tur: "sokum"; id: string } | { tur: "assembly"; rn: string } | null;
}

const ZAMAN_NOTU = {
  uygun: () => ({ metin: cevir("removed before this assembly"), ton: "neutral" as const }),
  ters: () => ({ metin: cevir("removed after this assembly: cannot be the same part at that time"), ton: "bad" as const }),
  bilinmiyor: () => ({ metin: cevir("dates not known"), ton: "neutral" as const }),
};

/** Tam (ya da stok) adayın satırı. Yakın aday tek başına da yazılabilir; blokta yakınlar `yakinMetinleri` ile kimlik ve riser başına birleşir. */
export function adayMetni(a: SureklilikAdayi): AdayGorunumu {
  const exact: EtiketGorunumu = { metin: cevir("exact"), ton: "info" };
  const rn = a.rn ?? "—";
  if (a.eslesme === "yakin") return { metin: yakinMetinleri([a])[0] ?? "", rozet: null, not: null, baglanti: null };
  if (a.kaynak === "stok") {
    const eslesme = a.stokEslesme === "po" ? cevir("PO matches") : cevir("trace matches");
    return { metin: `${cevir("Stock package {0} · {1}", a.paketNo === undefined ? "—" : pkgNo(a.paketNo), a.pn ?? "—")} · ${eslesme}`, rozet: null, not: null, baglanti: null };
  }
  if (a.kaynak === "mrb") {
    const metin = a.rn ? cevir("{0} · OEM record (MRB {1}) · {2}", a.rn, a.mrbSn ?? "—", yerAdi(a.yer)) : cevir("MRB {0} (not linked to a riser) · {1}", a.mrbSn ?? "—", yerAdi(a.yer));
    return { metin, rozet: exact, not: null, baglanti: null };
  }
  if (a.kaynak === "sokum") {
    // Plan D §5.1.4: "{R142} · Disassembly record · {Choke} · new ID {142-2} · removed {gün}"; yeni kimlik ve gün yalnız varsa.
    const parcalar = [cevir("{0} · Disassembly record · {1}", rn, yerAdi(a.yer)), ...(a.yeni ? [cevir("new ID {0}", a.yeni)] : []), ...(a.tarih ? [cevir("removed {0}", gunMetni(a.tarih))] : [])];
    return { metin: parcalar.join(" · "), rozet: exact, not: ZAMAN_NOTU[a.zaman ?? "bilinmiyor"](), baglanti: a.kayitId ? { tur: "sokum", id: a.kayitId } : null };
  }
  const parcalar = [cevir("{0} · assembly record · {1}", rn, yerAdi(a.yer)), ...(a.tarih ? [cevir("assembled {0}", gunMetni(a.tarih))] : [])];
  return {
    metin: parcalar.join(" · "),
    rozet: exact,
    not: a.celiski === false ? null : { metin: cevir("the same serial number is written on two assembly records"), ton: "bad" },
    baglanti: a.rn ? { tur: "assembly", rn: a.rn } : null,
  };
}

/** Yakın satırdaki kaynak adı (stok adayı yakın olmaz). */
function kaynakAdi(k: Exclude<SureklilikAdayi["kaynak"], "stok">): string {
  if (k === "mrb") return cevir("OEM record");
  if (k === "sokum") return cevir("Disassembly record");
  return cevir("assembly record");
}

/** Kaynakların yakın satırdaki sırası: "OEM record, Disassembly record". */
const YAKIN_KAYNAK_SIRASI: readonly Exclude<SureklilikAdayi["kaynak"], "stok">[] = ["mrb", "sokum", "montaj"];

/**
 * Yakın adaylar (öneri DEĞİL): aynı kimlik ve riser tek satırda, kaynakları birlikte:
 * "similar, not a suggestion: 9994875-6 (R142 · OEM record, Disassembly record)". Sıra sunucunun sırasıdır.
 */
export function yakinMetinleri(yakin: readonly SureklilikAdayi[]): string[] {
  const gruplar: { sn: string; yer: string; kaynaklar: Set<SureklilikAdayi["kaynak"]> }[] = [];
  for (const a of yakin) {
    if (a.kaynak === "stok") continue;
    const yer = a.rn ?? (a.mrbSn ? `MRB ${a.mrbSn}` : "—");
    const g = gruplar.find((x) => x.sn === a.sn && x.yer === yer);
    if (g) g.kaynaklar.add(a.kaynak);
    else gruplar.push({ sn: a.sn, yer, kaynaklar: new Set([a.kaynak]) });
  }
  return gruplar.map((g) => cevir("similar, not a suggestion: {0} ({1})", g.sn, `${g.yer} · ${YAKIN_KAYNAK_SIRASI.filter((k) => g.kaynaklar.has(k)).map(kaynakAdi).join(", ")}`));
}

/**
 * Tuzak cümlesi (sonuç `farkli` ya da `belirsiz`, aday olsa da olmasa da): tek karakter uzaktaki filo numaralarının sayısı ve
 * uyarı. Sayı 0 ise yalnız ikinci cümle. Öteki sonuçlarda yok.
 */
export function tuzakMetni(s: Pick<SureklilikSatiri, "sonuc"> & { adaylar: Pick<AdaySonucu, "yakinSayisi"> }): string | null {
  if (s.sonuc !== "farkli" && s.sonuc !== "belirsiz") return null;
  const uyari = cevir("A misread digit points to another riser: check the form before declaring.");
  return s.adaylar.yakinSayisi > 0 ? `${cevir("One character away from {0} other serial numbers in the fleet.", s.adaylar.yakinSayisi)} ${uyari}` : uyari;
}

/** Aday bloğunun içeriği: tam adaylar ("+n more" ile), yakınlar (yalnız tam yokken), stok adayları, "Not found…" ve tuzak cümlesi. */
export interface AdayBlogu {
  tam: AdayGorunumu[];
  fazla: string | null;
  stok: AdayGorunumu[];
  yakin: string[];
  bulunamadi: string | null;
  tuzak: string | null;
}
export function adayBlogu(s: Pick<SureklilikSatirYaniti, "sonuc" | "adaylar">): AdayBlogu {
  const hepsi = ADAY_SONUCLARI.includes(s.sonuc);
  // Eşit çıkan satırda yalnız başka montaj kaydındaki eşleşme gösterilir (sunucu da yalnız onu döner).
  const tam = hepsi ? s.adaylar.tam : s.adaylar.tam.filter((a) => a.kaynak === "montaj");
  const gizli = Math.max(0, s.adaylar.tamSayisi - s.adaylar.tam.length);
  return {
    tam: tam.map(adayMetni),
    fazla: gizli > 0 ? cevir("+{0} more", gizli) : null,
    stok: hepsi ? s.adaylar.stok.map(adayMetni) : [],
    yakin: hepsi && !tam.length ? yakinMetinleri(s.adaylar.yakin) : [],
    bulunamadi: hepsi && !tam.length ? cevir("Not found in any Disassembly record, MRB content or assembly record.") : null,
    tuzak: tuzakMetni(s),
  };
}

/* ------------------------------------------------------------ beyan ------------------------------------------------------------ */

/** Dört beyanın düğme adı (pencerenin başlığı da budur). */
export function beyanDugmeAdi(t: MontajBagTuru): string {
  if (t === "ayni") return cevir("Same part");
  if (t === "baska_riser") return cevir("From another riser…");
  if (t === "stok") return cevir("New from stock…");
  return cevir("Unknown");
}

/** Beyan çipi: aynı parça (ok) · başka riserdan (warn) · stoktan yeni (info; paket numarasıyla, paket silinmişse numarasız) · bilinmiyor. */
export function beyanCipi(b: Pick<MontajBagi, "tur" | "rn" | "paketNo">): EtiketGorunumu {
  if (b.tur === "ayni") return { metin: cevir("declared: same part"), ton: "ok" };
  if (b.tur === "baska_riser") return { metin: cevir("declared: from riser {0}", b.rn ?? "—"), ton: "warn" };
  if (b.tur === "stok") return { metin: b.paketNo != null ? cevir("declared: new from stock · {0}", pkgNo(b.paketNo)) : cevir("declared: new from stock"), ton: "info" };
  return { metin: cevir("declared: source unknown"), ton: "neutral" };
}

/** Beyan çipinin altındaki gri satırlar: "{kim} · {gün}" ve (varsa) not. */
export function beyanAltSatirlari(b: Pick<MontajBagi, "kim" | "ts" | "not">): string[] {
  const kim = [b.kim, b.ts ? gunMetni(b.ts) : null].filter(Boolean).join(" · ");
  return [kim, b.not?.trim() ?? ""].filter(Boolean);
}

/** Köken satırı Project documents'tan değiştirilmiş: uyarı cümlesi ("Declare again" ve "Remove declaration" ile); değilse null. */
export function kokenUyarisi(k: KokenDurumu | null | undefined): string | null {
  return k?.durum === "degismis" ? cevir("origin entry was changed on Project documents: now {0}", k.simdi ?? cevir("this riser")) : null;
}

/**
 * Beyan hücresinin düğmeleri: birimi olmayan satırda hiç yok · beyan varsa çip ve "Remove declaration" · sonuç `ayni` iken tek
 * küçük "Declare…" (aynı dört seçeneği açar) · öteki sonuçlarda dört düğme. Kayıt tamamlanmamışsa (`beyanAcik` false) düğmeler
 * pasif ve ipucu cümlesi; yetki (`mrb`) düğmenin kendisinde denetlenir.
 */
export type BeyanDugmeleri = { gorunum: "yok" } | { gorunum: "beyanli" } | { gorunum: "dort" | "tek"; pasif: boolean; ipucu: string | null };
export function beyanDugmeleri(s: Pick<SureklilikSatiri, "birimId" | "sonuc" | "bag">, beyanAcik: boolean): BeyanDugmeleri {
  if (!s.birimId) return { gorunum: "yok" };
  if (s.bag) return { gorunum: "beyanli" };
  return { gorunum: s.sonuc === "ayni" ? "tek" : "dort", pasif: !beyanAcik, ipucu: beyanAcik ? null : cevir("Continuity is declared after the assembly record is completed.") };
}

/** Kayıttaki birim (sayfanın montaj kaydından): formdaki rol harfi ve saklanan temiz kimlik. Bulunamazsa null. */
export function kayitBirimi(satirlar: MontajKaydiYaniti["satirlar"], birimId: string | null): { rol: MontajRol | null; sn: string | null } | null {
  if (!birimId) return null;
  for (const x of satirlar) for (const b of x.birimler) if (b.id === birimId) return { rol: b.rol, sn: b.sn };
  return null;
}

/**
 * Beyan gövdesindeki `sn` (bayat sayfa koruması: sunucu beyan kimliğiyle karşılaştırır). Ana gövdede (`MB`) takılan hücresinin
 * kimliği: sunucu da satırın takılan kimliğine bakar (kimlik SN kolonunda değil `po` ya da `diger`'de olabilir; KP-56). Hatlarda
 * kayıttaki birimin saklanan kimliği; birim sayfanın kaydında bulunamazsa takılan hücresinin kimliği.
 */
export const beyanSn = (s: Pick<SureklilikSatiri, "kalem" | "takilan">, birim: { sn: string | null } | null): string | null =>
  s.kalem === "MB" || !birim ? (s.takilan?.sn ?? null) : birim.sn;

/**
 * Pencerede "Role of this line" alanı: yalnız C&K satırında ve birimin FORMDAKİ rol harfi boşken (formda yazan aynen alınır; kişi
 * yalnız boş rolü doldurur). Formdaki harf bilinmiyorsa (`formRolu` undefined) yerleşimden çıkarılır: "kayit" ya da iki birim aynı
 * harf (`rol_cift`) formda harf olduğunu söyler.
 */
export function rolAlaniGerekli(s: Pick<SureklilikSatiri, "kalem" | "rolKaynagi" | "isaretler">, formRolu?: MontajRol | null): boolean {
  if (s.kalem !== "C" && s.kalem !== "K") return false;
  if (formRolu !== undefined) return formRolu === null;
  return s.rolKaynagi !== "kayit" && !isaretVar(s, "rol_cift");
}

/** "Same part" penceresiz yazılır; rol alanı gerekiyorsa küçük pencere açılır. Öteki üç beyan her zaman pencere açar. */
export const pencereGerekli = (t: MontajBagTuru, rolAlani: boolean): boolean => t !== "ayni" || rolAlani;

/**
 * "From another riser…" penceresinde riser girişinin ön-doldurması (KP-55): yalnız tek riserı gösteren tam aday SÖKÜM kaydından
 * geliyorsa, zamanı "uygun"sa ve satırda `kendi_degerine_yakin` işareti yoksa o riser; değilse boş. Yalnız MRB'den gelen tam
 * eşleşme ve yakın aday doldurmaz. Kişi "Confirm"e basmadan hiçbir şey yazılmaz.
 */
export const onDoldurmaRn = (s: Pick<SureklilikSatiri, "isaretler"> & { adaylar: Pick<AdaySonucu, "tam" | "tamSayisi"> }): string => beyanOnDoldurmaRn(s) ?? "";

/** Pencerenin alanları. Boş metin gönderilmez (null). */
export interface BeyanFormu {
  rn?: string;
  paketId?: string;
  not?: string;
  rol?: MontajRol | null;
}

/**
 * Beyan gövdesi: `tur` ve `sn` her zaman (sn null olabilir); `rn` yalnız "başka riserdan", `paketId` yalnız "stoktan yeni", `rol`
 * yalnız "aynı parça" ve "başka riserdan" penceresinde seçilmişse; `not` kırpılır, boşsa null.
 */
export function beyanGovdesi(tur: MontajBagTuru, sn: string | null, f: BeyanFormu = {}): MontajBagGovdesi {
  const g: MontajBagGovdesi = { tur, sn };
  if (tur === "baska_riser") g.rn = f.rn?.trim() || null;
  if (tur === "stok") g.paketId = f.paketId?.trim() || null;
  if ((tur === "ayni" || tur === "baska_riser") && f.rol) g.rol = f.rol;
  g.not = f.not?.trim() || null;
  return g;
}

/** "Declare again": kayıtlı beyan aynen yeniden gönderilir (sunucu Project documents'tan değişmiş köken satırını yeniden açar). */
export const yenidenBeyanGovdesi = (b: Pick<MontajBagi, "tur" | "rn" | "paketId" | "not">, sn: string | null): MontajBagGovdesi =>
  beyanGovdesi(b.tur, sn, { rn: b.rn ?? "", paketId: b.paketId ?? "", not: b.not ?? "" });

/**
 * Riser girişinin öneri listesi: kutuda adı geçen öteki riserlar (tam ve yakın adaylar, "not fitted back" listesindeki montaj
 * kayıtları), doğal sırayla; bu riser listede yoktur. Listede olmayan riser da yazılabilir.
 */
export function riserOnerileri(y: Pick<MontajSureklilikYaniti, "rn" | "satirlar" | "geriTakilmayan">): string[] {
  const rnler = new Set<string>();
  for (const s of y.satirlar) for (const a of [...s.adaylar.tam, ...s.adaylar.yakin]) if (a.rn) rnler.add(a.rn);
  for (const g of y.geriTakilmayan) for (const m of g.montajda) rnler.add(m.rn);
  rnler.delete(y.rn);
  return [...rnler].sort((a, b) => a.localeCompare(b, "en", { numeric: true }));
}

/**
 * "Stock package (optional)" seçenekleri: ilk seçenek "no package selected" (seçili gelen odur); satırın stok adayı olan paketler
 * önce gelir (seçili gelmez), sonra öteki paketler. Etiket "PKG-0007 · PN · iz · PO".
 */
export function paketSecenekleri(paketler: MontajSureklilikYaniti["stokPaketleri"], adaylar: readonly SureklilikAdayi[]): { deger: string; etiket: string }[] {
  const once = new Set(adaylar.filter((a) => a.kaynak === "stok" && a.paketId).map((a) => a.paketId));
  const sirali = [...paketler.filter((p) => once.has(p.id)), ...paketler.filter((p) => !once.has(p.id))];
  return [{ deger: "", etiket: cevir("no package selected") }, ...sirali.map((p) => ({ deger: p.id, etiket: [pkgNo(p.no), p.pn, p.iz?.trim() || "—", p.poNo?.trim() || "—"].join(" · ") }))];
}

/* ------------------------------------------------------------ küçük parçalar ------------------------------------------------------------ */

/** Sökümde çıkan küçük parça: "eski → yeni"; yeni kimlik yoksa yalnız eski; eski yoksa "— → yeni". */
export const cikanParcaMetni = (c: { eski: string | null; yeni: string | null }): string => (c.yeni ? `${c.eski ?? "—"} → ${c.yeni}` : (c.eski ?? "—"));
